import { Card, Col, Row, Spin, Tag } from "antd";
import { Fragment } from "react/jsx-runtime";
import Highcharts from "highcharts";
import "highcharts/modules/drilldown";
import "highcharts/modules/no-data-to-display";
import HighchartsReact from "highcharts-react-official";
import { useEffect, useRef, useState } from "react";

export const BoothSalesperMonthView = ({ data, loading }: any) => {
    const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
    const chartRef = useRef<HighchartsReact.RefObject>(null);
    const monthShortNames = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"
    ];
    const monthFullNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];
    const barColors = [
        '#E55050',
        '#008048',
        '#35B1E8',
        '#FFA51F',
        '#FEBEC8',
        '#4C5167',
        '#008044',
        '#35B1E8',
    ];;


    // ✅ Toggle months selection
    const handleMonthToggle = (month: string) => {
        setSelectedMonths((prev) =>
            prev.includes(month) ? prev.filter((m) => m !== month) : [...prev, month]
        );
    };

    // ✅ API hook call


    // ✅ Prepare Monthly Chart Data
    const monthlyDataFromApi =
        data?.GraphData?.map((item: any) => ({
            name: monthShortNames[item.GMonth - 1],
            y: item.TotalCount,
            revenue: item?.TotalRevenue,
            drilldown: monthFullNames[item.GMonth - 1],
        })) || [];

    // ✅ Prepare Drilldown Series (Daily sales)
    const drilldownSeriesFromApi =
        data?.DrilldownData
            ? Object.values(
                data.DrilldownData.reduce((acc: any, day: any) => {
                    const monthFull = monthFullNames[day.GMonth - 1];
                    if (!acc[monthFull]) {
                        acc[monthFull] = {
                            id: monthFull,
                            name: `Daily Sales - ${monthFull}`,
                            type: "column",
                            colors: barColors,
                            data: [],
                        };
                    }
                    acc[monthFull].data.push({
                        name: `${day.GDay} ${monthShortNames[day.GMonth - 1]}`,
                        y: day.TotalCount,          // ✅ use count for the column height
                        revenue: day.TotalRevenue,  // ✅ custom property for tooltip
                    });

                    return acc;
                }, {})
            )
            : [];

    // ✅ If user selects months manually → recalc totals
    const baseSeriesData =
        selectedMonths.length === 0
            ? monthlyDataFromApi
            : selectedMonths.map((shortMonth) => {
                const monthIndex = monthShortNames.indexOf(shortMonth) + 1;
                const total =
                    data?.DrilldownData?.filter(
                        (d: any) => d.GMonth === monthIndex
                    ).reduce((sum: number, d: any) => sum + d.TotalRevenue, 0) || 0;

                return { name: shortMonth, y: total };
            });

    // ✅ Total Revenue across all months
    const totalRevenue =
        data?.GraphData?.reduce(
            (sum: number, item: any) => sum + (item.TotalRevenue || 0),
            0
        ) || 0;

    // ✅ Highcharts Config
    const chartOptions: Highcharts.Options = {
        chart: { type: "column" },
        title: {
            text:
                selectedMonths.length === 0
                    ? "Shop Sales per Month"
                    : `Combined Sales for: ${selectedMonths.join(", ")}`,
        },
        lang: { noData: "No data found" },
        subtitle: {
            text: `Total Revenue: KWD ${totalRevenue.toLocaleString(undefined, {
                minimumFractionDigits: 2,
            })}`,
            style: {
                fontSize: '9px', // 🔹 set your desired font size
                fontWeight: 'bold', // optional: make it bold
                color: '#333', // optional: set color
            },
        },
        credits: { enabled: false },
        xAxis: {
            type: "category", labels: {
                style: {
                    color: "#000", // ✅ x labels black
                    fontSize: "11px",
                },
            },
        },
        yAxis: {
            title: { text: "Total Sold", style: { fontSize: "11px", color: "black" } },
            labels: { style: { fontSize: "11px", color: "black" } },
        },
        legend: { enabled: false },
        colors: barColors, // ✅ Apply alternating colors globally

        plotOptions: {
            series: {
                borderWidth: 0,
                colors: barColors,
                dataLabels: {
                    enabled: true,
                     format: "{point.y:.0f}", // ✅ only top value
                    style: {
                        color: "black",
                        fontSize: "11px",
                        fontWeight: "bold",
                    },
                },
            },
        },
        tooltip: {
            headerFormat: '<span style="font-size:11px">{series.name}</span><br/>',
            pointFormatter: function (this: any) {
                return `
      <span style="color:${this.color}">${this.name}</span><br/>
      <b>Total Count:</b> ${this.y}<br/>
      <b>Total Revenue:</b> KWD ${(this.revenue || 0)}
    `;
            }
        },
        series: [
            {
                name:
                    selectedMonths.length === 0
                        ? "Monthly Sales"
                        : `Total Sales (${selectedMonths.length} month${selectedMonths.length > 1 ? "s" : ""
                        })`,
                type: "column",
                data: baseSeriesData,
            },
        ],
        ...(selectedMonths.length === 0 && {
            drilldown: {
                series: drilldownSeriesFromApi,
                activeAxisLabelStyle: {
                    textDecoration: "none",
                    color: "black",
                },
                activeDataLabelStyle: {
                    textDecoration: "none",
                    color: "black",
                },
            },
        }),
    } as any;

    // ✅ Show/Hide Loading
    useEffect(() => {
        const chart = chartRef.current?.chart;
        if (chart) {
            if (loading) {
                chart.showLoading("Loading data...");
            } else {
                chart.hideLoading();
            }
        }
    }, [loading]);

    return (
        <Fragment>
            <Card>
                <Row gutter={[16, 16]} className="month_tabs">
                    {monthShortNames.map((month) => (
                        <Col key={month}>
                            <Tag
                                color={selectedMonths.includes(month) ? "yellow" : "default"}
                                style={{ cursor: "pointer" }}
                                onClick={() => handleMonthToggle(month)}
                            >
                                {month}
                            </Tag>
                        </Col>
                    ))}
                </Row>
                <Spin spinning={loading}>
                    <HighchartsReact
                        ref={chartRef}
                        highcharts={Highcharts}
                        options={chartOptions}
                    />
                </Spin>
            </Card>
        </Fragment>
    );
};
